// Live custom-mix reframe client (spec §4a.3 / §4a.7). The ONLY runtime model call.
// Builds the quantized cache key, calls POST /api/reframe, and — when the endpoint
// is gated off (503), errors, or times out — falls back to the design's nearest-
// precomputed SIMULATION so C5 stays usable and the demo stays deterministic.

import type { ReframeBlock, ReframeResult } from "./types";

export type ReframeSource = "live" | "cache" | "sim";

export interface ReframeOutcome {
  result: ReframeResult;
  source: ReframeSource;
  disabled: boolean; // the server reported the live path is gated off
}

const cache = new Map<string, ReframeResult>();

export function buildCacheKey(
  rf: ReframeBlock,
  targets: Record<string, number>,
  structs: Record<string, string>
): string {
  const ax = rf.axes.map((a) => `${a}:${Math.round(targets[a] || 0)}`).join("|");
  const st = Object.entries(structs).map(([k, v]) => `${k}:${v}`).join("|");
  return `v1|${ax}|${st}`;
}

/** Design's synthReframe: nearest precomputed preset (L2 over requested vectors). */
export function simulate(
  rf: ReframeBlock,
  targets: Record<string, number>,
  structs: Record<string, string>
): ReframeResult {
  const cand = Object.values(rf.precomputed).filter((p) => !p.as_read);
  let best = cand[0], bestD = Infinity;
  for (const p of cand) {
    let d = 0;
    for (const a of rf.axes) {
      const dv = (targets[a] || 0) - (Number(p.requested_target[a]) || 0);
      d += dv * dv;
    }
    if (d < bestD) { bestD = d; best = p; }
  }
  const residuals: Record<string, number> = {};
  let on = 0;
  for (const a of rf.axes) {
    const r = Math.abs((targets[a] || 0) - (Number(best.achieved_scores[a]) || 0));
    residuals[a] = r;
    if (r === 0) on++;
  }
  return {
    ...best,
    profile_id: "__custom", display: "Custom mix", _custom: true, _closest: best.display,
    requested_target: { ...targets }, structural_targets: { ...structs }, residuals,
    manipulation_check: {
      intended_direction: "custom",
      achieved_direction: best.manipulation_check.achieved_direction,
      axes_on_target: on, axes_total: rf.axes.length, landed: on >= rf.axes.length / 2,
    },
    invariant: best.invariant,
  };
}

export interface LiveCtx {
  session_id: string;
  participant_id: string;
  join_gva_id: number;
  target_outlet: string;
}

export async function requestReframe(
  rf: ReframeBlock,
  ctx: LiveCtx,
  targets: Record<string, number>,
  structs: Record<string, string>,
  timeoutMs = 60000
): Promise<ReframeOutcome> {
  const key = buildCacheKey(rf, targets, structs);
  if (cache.has(key)) return { result: cache.get(key)!, source: "cache", disabled: false };

  const payload = {
    session_id: ctx.session_id, participant_id: ctx.participant_id,
    join_gva_id: ctx.join_gva_id, target_outlet: ctx.target_outlet,
    reframe_cache_key: key,
    target: { kind: "custom", targets, structural_targets: structs },
  };

  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const r = await fetch(rf.live.endpoint, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload), signal: controller.signal,
    });
    clearTimeout(t);
    if (r.status === 503) {
      // gated off — simulate
      const sim = simulate(rf, targets, structs);
      cache.set(key, sim);
      return { result: sim, source: "sim", disabled: true };
    }
    if (r.ok) {
      const result = (await r.json()) as ReframeResult;
      // client-side invariant re-check before the caller commits to P1
      if (result.invariant && result.invariant.ok === false) {
        return { result, source: "live", disabled: false };
      }
      cache.set(key, result);
      return { result, source: "live", disabled: false };
    }
    throw new Error("reframe http " + r.status);
  } catch {
    clearTimeout(t);
    const sim = simulate(rf, targets, structs);
    cache.set(key, sim);
    return { result: sim, source: "sim", disabled: false };
  }
}
